"use client";

import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type HTMLMotionProps,
  type Variants,
} from "motion/react";
import { Check, Loader2, X } from "lucide-react";
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type PointerEvent,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { EASE_OUT, SPRING_PRESS_SETTLED, SPRING_SWAP } from "../../../lib/ease";
import { cn } from "../../../lib/utils";
import { useHoverCapable } from "../../../lib/hooks/use-hover-capable";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "outline" | "destructive" | "icon";
export type ButtonSize = "2xs" | "xs" | "sm" | "md" | "lg" | "icon";
export type ButtonState = "idle" | "loading" | "success" | "error";

/**
 * `motion/react` claims the drag props for its own gesture, which it only runs
 * when `drag` is set — and this button never sets it. Native HTML5 drag is a
 * real requirement for an icon button (a column reorder grip is one), so those
 * three names carry React's event types here rather than motion's.
 */
type NativeDragProps = Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "onDrag" | "onDragStart" | "onDragEnd"
>;

export interface ButtonProps
  extends Omit<HTMLMotionProps<"button">, "children" | keyof NativeDragProps>,
    NativeDragProps {
  variant?: ButtonVariant;
  /**
   * Step on the v2 control ladder. `variant="icon"` reads the same name off the
   * icon ladder instead of the label ladder — a square box with a constant
   * 40px hit area — because an icon button is sized by its glyph, not its text.
   */
  size?: ButtonSize;
  pressScale?: number;
  /**
   * Spawn a Material-style ripple from the press point. On by default; pass
   * `false` for a control where the spread would fight its own motion.
   */
  ripple?: boolean;
  /**
   * Toggled-on look, for a control that holds a state — an open menu trigger,
   * an engaged filter. Reads as pressed rather than hovered.
   */
  active?: boolean;
  children?: ReactNode;
  /**
   * Optional animated status. When set, the label and a leading status icon
   * animate between idle, loading, success, and error. Leaving it undefined
   * renders a plain button.
   */
  state?: ButtonState;
  loadingText?: ReactNode;
  successText?: ReactNode;
  errorText?: ReactNode;
  /** Leading icon shown in the idle state. Animates in and out. */
  icon?: ReactNode;
}

type Ripple = { id: number; x: number; y: number; size: number };

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: "bg-v2-primary text-v2-primary-foreground hover:bg-v2-primary/90",
  secondary: "border border-v2-border bg-v2-card text-v2-foreground hover:border-v2-border",
  ghost: "text-v2-muted-foreground hover:text-v2-foreground hover:bg-v2-primary/5",
  outline:
    "border border-v2-border bg-transparent text-v2-foreground hover:bg-v2-primary/5",
  destructive: "bg-v2-destructive text-v2-destructive-foreground hover:bg-v2-destructive/90",
  icon: "text-v2-muted-foreground hover:bg-v2-accent hover:text-v2-accent-foreground data-[active=true]:bg-v2-accent data-[active=true]:text-v2-accent-foreground",
};

/**
 * All sizes share the v2 radius rather than a pill. `rounded-full` made buttons
 * the only chrome in the set that was not on the radius scale, and it read as
 * foreign next to inputs, menus and cards at 8px.
 *
 * `2xs` and `xs` carry no label ladder of their own — a 24px box cannot hold
 * text — so they fall back to `sm`. They exist for `variant="icon"`, which
 * reads `ICON_SIZE_CLASS` instead.
 */
export const BUTTON_SIZE_CLASS: Record<ButtonSize, string> = {
  "2xs": "h-8 px-3 text-v2-sm gap-1.5 rounded-v2-lg",
  xs: "h-8 px-3 text-v2-sm gap-1.5 rounded-v2-lg",
  sm: "h-8 px-3 text-v2-sm gap-1.5 rounded-v2-lg",
  md: "h-9 px-4 text-v2-base gap-2 rounded-v2-lg",
  lg: "h-10 px-5 text-v2-base gap-2 rounded-v2-lg",
  icon: "size-9 rounded-v2-lg",
};

/**
 * The icon ladder: visible box paired with the padding that lifts every step
 * back to a 40px hit area. Shrinking the glyph must not shrink the target, so
 * the smaller steps grow a pseudo-element instead — 24px, the WCAG 2.2 floor,
 * still hits at 40. `icon` is the legacy name for the `md` box, kept so an
 * existing `size="icon"` call site kept its dimensions when the icon button
 * folded into this component.
 */
const ICON_SIZE_CLASS: Record<ButtonSize, string> = {
  "2xs": "size-6 before:-inset-2",
  xs: "size-7 before:-inset-1.5",
  sm: "size-8 before:-inset-1",
  md: "size-9 before:-inset-0.5",
  lg: "size-10",
  icon: "size-9 before:-inset-0.5",
};

/** Press response for a small square target, gentler than the label default. */
/**
 * How far a press shrinks the control. Kept shallow: the dip only has to
 * register as feedback, and a deep one is disproportionate on a wide trigger,
 * where the same ratio travels far more pixels than it does on an icon.
 */
const ICON_PRESS_SCALE = 0.96;
const LABEL_PRESS_SCALE = 0.97;

const CASCADE_STAGGER = 0.025;
const ROLL_BLUR = "blur(6px)";

const CASCADE_LETTER_VARIANTS: Variants = {
  initial: { opacity: 0, y: "105%", filter: ROLL_BLUR },
  animate: (delay: number = 0) => ({
    opacity: 1,
    y: "0%",
    filter: "blur(0px)",
    transition: { ...SPRING_SWAP, delay },
  }),
  exit: (delay: number = 0) => ({
    opacity: 0,
    y: "-105%",
    filter: ROLL_BLUR,
    transition: { duration: 0.16, ease: EASE_OUT, delay: delay * 0.5 },
  }),
};

const ICON_VARIANTS: Variants = {
  // Width collapses too, so the icon adds/removes its own space smoothly
  // instead of popping the row width in a single frame.
  initial: { opacity: 0, width: 0, scale: 0.7, filter: ROLL_BLUR },
  animate: {
    opacity: 1,
    width: "1.5rem",
    scale: 1,
    filter: "blur(0px)",
    transition: SPRING_SWAP,
  },
  exit: {
    opacity: 0,
    width: 0,
    scale: 0.7,
    filter: ROLL_BLUR,
    transition: { duration: 0.16, ease: EASE_OUT },
  },
};

function IconSlot({ keyId, children }: { keyId: string; children: ReactNode }) {
  const reduce = useReducedMotion();
  return (
    <motion.span
      key={keyId}
      variants={ICON_VARIANTS}
      initial={reduce ? { opacity: 0 } : "initial"}
      animate={reduce ? { opacity: 1 } : "animate"}
      exit={reduce ? { opacity: 0 } : "exit"}
      transition={reduce ? { duration: 0.15 } : undefined}
      className="inline-grid shrink-0 place-items-center overflow-hidden"
    >
      {children}
    </motion.span>
  );
}

function TextSlot({ value, children }: { value: string; children: ReactNode }) {
  const reduce = useReducedMotion();
  const measureRef = useRef<HTMLSpanElement>(null);
  const [width, setWidth] = useState<number>();
  const label = typeof children === "string" ? children : null;
  const cascade = label !== null && !reduce;

  // Measure strings with the same per-letter layout as the cascade. Measuring
  // the whole string preserves kerning, which can make it narrower than the
  // inline-block letters and clip the final glyph during the width animation.
  //
  // Deliberately dep-free: the width depends on rendered text, fonts and
  // container size, none of which is expressible as a dependency. The
  // functional update returns the current value when nothing moved, and React
  // bails out of an identical state, so this settles rather than looping.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const nextWidth = measureRef.current?.offsetWidth;
    if (!nextWidth) return;
    setWidth((current) => (current === nextWidth ? current : nextWidth));
  });

  return (
    <motion.span
      initial={false}
      animate={{ width }}
      transition={reduce ? { duration: 0 } : SPRING_SWAP}
      className="relative inline-block overflow-hidden whitespace-nowrap align-bottom"
    >
      <span
        ref={measureRef}
        aria-hidden
        className="invisible inline-block whitespace-nowrap"
      >
        {cascade
          ? label.split("").map((char, index) => (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: position is the slot identity.
                key={index}
                className="inline-block whitespace-pre"
              >
                {char}
              </span>
            ))
          : children}
      </span>

      {cascade ? (
        <>
          <span className="sr-only">{label}</span>
          <AnimatePresence initial={false}>
            <motion.span
              key={`cascade-${value}`}
              aria-hidden
              initial="initial"
              animate="animate"
              exit="exit"
              className="absolute left-0 top-0 inline-block whitespace-pre"
            >
              {label.split("").map((char, index) => (
                <motion.span
                  // biome-ignore lint/suspicious/noArrayIndexKey: position is the slot identity.
                  key={index}
                  custom={index * CASCADE_STAGGER}
                  variants={CASCADE_LETTER_VARIANTS}
                  className="inline-block whitespace-pre will-change-[opacity,filter,transform]"
                >
                  {char}
                </motion.span>
              ))}
            </motion.span>
          </AnimatePresence>
        </>
      ) : (
        <AnimatePresence initial={false}>
          <motion.span
            key={`text-${value}`}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 14, filter: ROLL_BLUR }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -14, filter: ROLL_BLUR }}
            transition={reduce ? { duration: 0.15 } : SPRING_SWAP}
            className="absolute left-0 top-0 inline-block will-change-[opacity,filter,transform]"
          >
            {children}
          </motion.span>
        </AnimatePresence>
      )}
    </motion.span>
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "primary",
      size = "md",
      pressScale,
      ripple = true,
      active,
      className,
      children,
      state,
      loadingText = "Loading",
      successText = "Done",
      errorText = "Try again",
      icon,
      disabled,
      onPointerDown,
      onDrag,
      onDragStart,
      onDragEnd,
      ...rest
    },
    ref,
  ) {
    const reduce = useReducedMotion();
    const canHover = useHoverCapable();
    const [ripples, setRipples] = useState<Ripple[]>([]);
    const nextId = useRef(0);

    const isIcon = variant === "icon";
    const resolvedPressScale = pressScale ?? (isIcon ? ICON_PRESS_SCALE : LABEL_PRESS_SCALE);
    const stateful = state !== undefined;

    /**
     * The DOM receives React's drag events; `motion.button` types those three
     * names for its own gesture, which needs `drag` to be set and never is
     * here. One cast at the single spread site keeps the public props honest.
     */
    const dragHandlers = { onDrag, onDragEnd, onDragStart } as unknown as Pick<
      HTMLMotionProps<"button">,
      "onDrag" | "onDragEnd" | "onDragStart"
    >;
    const isBusy = state === "loading";
    const stateText =
      state === "loading"
        ? loadingText
        : state === "success"
          ? successText
          : state === "error"
            ? errorText
            : children;
    const textKey =
      typeof stateText === "string" ? `${state}-${stateText}` : String(state);

    const handlePointerDown = useCallback(
      (event: PointerEvent<HTMLButtonElement>) => {
        if (ripple && !reduce) {
          const rect = event.currentTarget.getBoundingClientRect();
          const rippleSize = Math.max(rect.width, rect.height) * 2;
          const id = nextId.current++;
          setRipples((prev) => [
            ...prev,
            {
              id,
              x: event.clientX - rect.left,
              y: event.clientY - rect.top,
              size: rippleSize,
            },
          ]);
        }
        onPointerDown?.(event);
      },
      [ripple, reduce, onPointerDown],
    );

    return (
      <motion.button
        ref={ref}
        type="button"
        disabled={stateful ? disabled || isBusy : disabled}
        aria-busy={isBusy || undefined}
        aria-pressed={isIcon && active !== undefined ? active : undefined}
        data-active={active ? "true" : undefined}
        whileTap={reduce ? undefined : { scale: resolvedPressScale }}
        whileHover={reduce || !canHover || stateful ? undefined : { scale: 1.02 }}
        transition={SPRING_PRESS_SETTLED}
        onPointerDown={handlePointerDown}
        className={cn(
          "inline-flex items-center justify-center font-medium select-none",
          "transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-ring focus-visible:ring-offset-2 ring-offset-v2-background",
          "disabled:pointer-events-none disabled:opacity-50",
          /*
           * `relative` only — never `overflow-hidden` here. The ripple layer
           * below clips itself, and clipping the button would also clip the
           * icon variant's `before:` pseudo-element, which is the whole
           * mechanism holding its 40px hit area open.
           */
          "relative",
          isIcon && "before:absolute before:content-['']",
          VARIANT_CLASS[variant],
          isIcon ? ICON_SIZE_CLASS[size] : BUTTON_SIZE_CLASS[size],
          isIcon && "rounded-v2-lg",
          className,
        )}
        {...dragHandlers}
        {...rest}
      >
        {ripple && !reduce ? (
          <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]">
            <AnimatePresence>
              {ripples.map((r) => (
                <motion.span
                  key={r.id}
                  className="absolute rounded-full bg-current"
                  style={{
                    left: r.x,
                    top: r.y,
                    width: r.size,
                    height: r.size,
                    x: "-50%",
                    y: "-50%",
                  }}
                  initial={{ scale: 0.05, opacity: 0.3 }}
                  animate={{ scale: 1, opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.6, ease: EASE_OUT }}
                  onAnimationComplete={() =>
                    setRipples((prev) => prev.filter((x) => x.id !== r.id))
                  }
                />
              ))}
            </AnimatePresence>
          </span>
        ) : null}
        {stateful ? (
          <span
            aria-live="polite"
            className="relative inline-flex items-center justify-center overflow-hidden"
          >
            <AnimatePresence initial={false}>
              {state === "loading" ? (
                <IconSlot keyId="loading-icon">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </IconSlot>
              ) : null}
              {state === "success" ? (
                <IconSlot keyId="success-icon">
                  <Check className="h-4 w-4" />
                </IconSlot>
              ) : null}
              {state === "error" ? (
                <IconSlot keyId="error-icon">
                  <X className="h-4 w-4" />
                </IconSlot>
              ) : null}
            </AnimatePresence>

            <TextSlot value={textKey}>{stateText}</TextSlot>

            <AnimatePresence initial={false}>
              {state === "idle" && icon ? (
                <IconSlot keyId="idle-icon">{icon}</IconSlot>
              ) : null}
            </AnimatePresence>
          </span>
        ) : (
          children
        )}
      </motion.button>
    );
  },
);
